<script setup>
import { inject, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import MetricCard from '../components/MetricCard.vue';
import SparklineChart from '../components/SparklineChart.vue';
import EvolutionChart from '../components/EvolutionChart.vue';
import StatusDonutChart from '../components/StatusDonutChart.vue';
import QuickStatsCard from '../components/QuickStatsCard.vue';

const { t } = useI18n();

const {
  summaryData,
  conversationData,
  conversationMetrics,
  totalAgentsOnline,
  conversationsChange,
  resolutionsChange,
  kanbanSummary,
  csatMetrics,
  botMetrics,
  slaMetrics,
  campaignData,
  aiAgentData,
  crmData,
  isLoading,
} = inject('analyticsData');

// Calculate total messages
const totalMessages = computed(() => {
  if (!summaryData.value) return 0;
  return (
    (summaryData.value.incoming_messages_count || 0) +
    (summaryData.value.outgoing_messages_count || 0)
  );
});

// Format large numbers
function formatNumber(num) {
  if (!num) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString('pt-BR');
}

// Format time in seconds to human readable
function formatTime(seconds) {
  if (!seconds || seconds === 0) return '0m';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}.${Math.floor(minutes / 6)}h`;
  return `${minutes}m`;
}

// Conversation status data for donut
const statusData = computed(() => {
  if (!summaryData.value) return [];
  const total = summaryData.value.conversations_count || 0;
  const resolved = summaryData.value.resolutions_count || 0;
  const open = conversationMetrics.value.open || 0;
  const pending = conversationMetrics.value.pending || 0;
  const snoozed = total - resolved - open - pending;

  return [
    { label: 'Abertas', value: open, color: '#3b82f6' },
    { label: 'Resolvidas', value: resolved, color: '#22c55e' },
    { label: 'Pendentes', value: pending, color: '#f59e0b' },
    { label: 'Adiadas', value: Math.max(0, snoozed), color: '#6b7280' },
  ];
});

// CSAT percentage
const csatPercentage = computed(() => {
  if (!csatMetrics.value) return '0';
  return (csatMetrics.value.satisfaction_score || 0).toFixed(1);
});

// Pipeline value
const pipelineValue = computed(() => {
  const value =
    crmData.value?.total_value || kanbanSummary.value?.total_value || 0;
  return `R$ ${formatNumber(value)}`;
});

// SLA hit rate
const slaHitRate = computed(() => {
  if (!slaMetrics.value) return '0';
  return slaMetrics.value.hit_rate || 0;
});

// AI Agents metrics
const aiAgentExecutions = computed(() => {
  if (!aiAgentData.value) return 0;
  return aiAgentData.value.total_executions || 0;
});

const aiAgentTokens = computed(() => {
  if (!aiAgentData.value) return 0;
  return aiAgentData.value.total_tokens || 0;
});

// Campaign metrics
const campaignsSent = computed(() => {
  if (!campaignData.value) return 0;
  return campaignData.value.total_sent || 0;
});

const activeCampaigns = computed(() => {
  if (!campaignData.value) return 0;
  return campaignData.value.active || 0;
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Real-time Section -->
    <div
      class="flex items-center justify-between px-4 py-3 rounded-xl border border-n-weak bg-n-solid-2"
    >
      <div class="flex items-center gap-6">
        <span class="text-sm font-medium text-n-slate-11">Em tempo real</span>
        <div class="flex items-center gap-4">
          <span class="text-sm">
            <span class="font-semibold text-n-blue-11">{{
              conversationMetrics.open
            }}</span>
            <span class="text-n-slate-11 ml-1">abertas</span>
          </span>
          <span class="text-sm">
            <span class="font-semibold text-n-amber-11">{{
              conversationMetrics.unattended
            }}</span>
            <span class="text-n-slate-11 ml-1">{{
              $t('ANALYTICS_DASHBOARD.REALTIME.UNATTENDED')
            }}</span>
          </span>
          <span class="text-sm">
            <span class="font-semibold text-n-ruby-11">{{
              conversationMetrics.unassigned
            }}</span>
            <span class="text-n-slate-11 ml-1">{{
              $t('ANALYTICS_DASHBOARD.REALTIME.UNASSIGNED')
            }}</span>
          </span>
        </div>
      </div>
      <div class="flex items-center gap-2 text-sm text-n-slate-11">
        <span class="i-lucide-users text-base" />
        <span>{{ totalAgentsOnline }}
          {{ $t('ANALYTICS_DASHBOARD.REALTIME.AGENTS_ONLINE') }}</span>
      </div>
    </div>

    <!-- Main Metrics Cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <MetricCard
        icon="i-lucide-message-square"
        :title="$t('ANALYTICS_DASHBOARD.OVERVIEW.TOTAL_CONVERSATIONS')"
        :value="formatNumber(summaryData?.conversations_count || 0)"
        :subtitle="`${formatNumber(totalMessages)} ${$t('ANALYTICS_DASHBOARD.OVERVIEW.MESSAGES')}`"
        :change="conversationsChange"
        :sparkline-data="conversationData"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-check-circle"
        :title="$t('ANALYTICS_DASHBOARD.OVERVIEW.RESOLVED')"
        :value="formatNumber(summaryData?.resolutions_count || 0)"
        :subtitle="formatTime(summaryData?.avg_resolution_time)"
        :change="resolutionsChange"
        :sparkline-data="conversationData"
        color="green"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-clock"
        :title="$t('ANALYTICS_DASHBOARD.OVERVIEW.RESPONSE_TIME')"
        :value="formatTime(summaryData?.avg_first_response_time)"
        :subtitle="formatTime(summaryData?.avg_resolution_time)"
        :loading="isLoading"
      />
      <MetricCard
        icon="i-lucide-users"
        :title="$t('ANALYTICS_DASHBOARD.OVERVIEW.ACTIVE_CONTACTS')"
        :value="
          formatNumber(
            summaryData?.contacts_count || summaryData?.conversations_count || 0
          )
        "
        :subtitle="$t('ANALYTICS_DASHBOARD.OVERVIEW.SELECTED_PERIOD')"
        :loading="isLoading"
      />
    </div>

    <!-- Charts Row -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div class="lg:col-span-2">
        <EvolutionChart
          :data="conversationData"
          :loading="isLoading"
          :title="$t('ANALYTICS_DASHBOARD.OVERVIEW.EVOLUTION')"
          :subtitle="$t('ANALYTICS_DASHBOARD.OVERVIEW.EVOLUTION_SUBTITLE')"
        />
      </div>
      <StatusDonutChart
        :data="statusData"
        :loading="isLoading"
        :title="$t('ANALYTICS_DASHBOARD.OVERVIEW.BY_STATUS')"
      />
    </div>

    <!-- Quick Stats Row 1 -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
      <QuickStatsCard
        icon="i-lucide-star"
        title="CSAT"
        :value="`${csatPercentage}%`"
        :subtitle="`${csatMetrics?.total || 0} ${$t('ANALYTICS_DASHBOARD.OVERVIEW.RESPONSES')}`"
        color="amber"
      />
      <QuickStatsCard
        icon="i-lucide-shield-check"
        title="SLA"
        :value="`${slaHitRate}%`"
        :subtitle="`${slaMetrics?.hit || 0} de ${slaMetrics?.total || 0}`"
        color="green"
      />
      <QuickStatsCard
        icon="i-lucide-bot"
        title="Bots"
        :value="`${botMetrics?.resolution_rate || 0}%`"
        :subtitle="`${botMetrics?.resolutions || 0} ${$t('ANALYTICS_DASHBOARD.OVERVIEW.RESOLVED_LOWER')}`"
        color="purple"
      />
      <QuickStatsCard
        icon="i-lucide-briefcase"
        title="Pipeline"
        :value="pipelineValue"
        :subtitle="`${crmData?.total_deals || kanbanSummary?.total_deals || 0} ${$t('ANALYTICS_DASHBOARD.OVERVIEW.DEALS')}`"
        color="blue"
      />
    </div>

    <!-- Quick Stats Row 2: Campaigns & AI -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
      <QuickStatsCard
        icon="i-lucide-megaphone"
        title="Campanhas"
        :value="String(activeCampaigns)"
        :subtitle="`${formatNumber(campaignsSent)} enviadas`"
        color="pink"
      />
      <QuickStatsCard
        icon="i-lucide-sparkles"
        title="AI Agents"
        :value="String(aiAgentData?.active || 0)"
        :subtitle="`${formatNumber(aiAgentExecutions)} execuções`"
        color="violet"
      />
      <QuickStatsCard
        icon="i-lucide-coins"
        title="Tokens IA"
        :value="formatNumber(aiAgentTokens)"
        :subtitle="`${aiAgentData?.total || 0} agentes`"
        color="cyan"
      />
      <QuickStatsCard
        icon="i-lucide-calendar"
        title="Agendamentos"
        :value="String(crmData?.appointments?.today || 0)"
        :subtitle="`${crmData?.appointments?.upcoming || 0} próximos`"
        color="teal"
      />
    </div>
  </div>
</template>
